import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback, startTransition } from 'react';
import { MODES, TOKENS, VARIABLE_GROUPS, TOGGLES } from './data';
import { projectsApi, chaptersApi, charactersApi, variablesApi, chatApi } from './api';
import { readResumeIntent, clearResumeIntent } from './resumeIntent';
import { toast } from 'sonner';

import { useTheme } from './store/useTheme';
import { useChatStream } from './store/useChatStream';
import { useNovelBuild } from './store/useNovelBuild';

const Ctx = createContext(null);

/** Compute word count from raw content string when the stored `words` field is stale/zero. */
function _wordsFromContent(content) {
  if (!content || !content.trim()) return 0;
  return content.trim().split(/\s+/).filter(Boolean).length;
}

function initialVariables() {
  const obj = {};
  VARIABLE_GROUPS.forEach((g) => g.items.forEach((it) => { obj[it.id] = it.value; }));
  return obj;
}

const DEFAULT_TOGGLES = { tense: 'Past', pov: '3rd Limited', surprise: 30, heart: 60, density: 50 };

export function ProWriterProvider({ children }) {
  // ----- Theme -----
  const { themeMode, setThemeMode } = useTheme();

  // ----- Project -----
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // ----- Mode / Toggles / Variables -----
  const [activeMode, setActiveMode] = useState('draft');
  const [variables, setVariables] = useState(initialVariables);
  const [toggles, setToggles] = useState(DEFAULT_TOGGLES);

  // ----- Chapters / Characters -----
  // iter55w — Start with empty arrays. The bootstrap useEffect loads
  // the user's real project and setChapters/setCharacters replaces
  // these immediately. Prior code seeded these with SEED_CHAPTERS +
  // SEED_CHARACTERS so first-time users saw a flash of demo data
  // before their empty project loaded. Now the panels stay clean.
  const [chapters, setChapters] = useState([]);
  const [activeChapterId, setActiveChapterId] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [activeCharacterId, setActiveCharacterId] = useState(null);

  // ----- Tokens (backed by /api/usage/me — falls back to seeded TOKENS
  //        when the user is unauthenticated or the endpoint is unreachable) -----
  const [tokens, setTokens] = useState(TOKENS);
  const tokenPct = useMemo(() => (tokens.budget > 0 ? Math.round((tokens.used / tokens.budget) * 100) : 0), [tokens]);
  const addTokens = (amt) => setTokens((t) => ({ ...t, budget: t.budget + amt }));

  // iter-53n++ — Live token refresh. Fetches /api/usage/me and merges
  // {used, budget, remaining} into the tokens bag. Silent on error so
  // demo mode / unauth doesn't spam the UI with toasts.
  const refreshTokens = useCallback(async () => {
    try {
      const { usageApi } = await import('./api');
      const u = await usageApi.me();
      setTokens((prev) => ({
        ...prev,
        used:      u.used   || 0,
        budget:    u.budget || 0,
        remaining: u.remaining || 0,
        rateIn:  u.rate_in_usd_per_million  || prev.rateIn,
        rateOut: u.rate_out_usd_per_million || prev.rateOut,
      }));
    } catch { /* unauth / offline / demo → keep seed */ }
  }, []);
  // Refresh once on mount and every 60s while the tab is visible.
  useEffect(() => {
    let cancelled = false;
    refreshTokens();
    const iv = setInterval(() => { if (!document.hidden && !cancelled) refreshTokens(); }, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [refreshTokens]);

  // ----- Chat & streaming generation (extracted into hooks) -----
  const chatState = useChatStream(project, activeChapterId);

  // ------------------------------------------------------------------
  // loadProject: pull the full project bundle (chapters + variables + chat)
  // ------------------------------------------------------------------
  const loadProject = useCallback(async (id, { silent = false } = {}) => {
    try {
      const [p, chs, chars, vs, msgs] = await Promise.all([
        projectsApi.get(id),
        chaptersApi.list(id),   // content excluded server-side — keeps payload small
        charactersApi.list(id),
        variablesApi.get(id),
        chatApi.list(id).catch(() => []),
      ]);

      // Wrap all state updates in startTransition so React yields between
      // batches — the browser stays interactive even on mobile during load.
      startTransition(() => {
        setProject(p);
        setChapters(chs.map((c) => ({
          id: c.id, number: c.number, title: c.title || `Chapter ${c.number}`,
          words: c.words || 0,   // server-computed; _wordsFromContent not needed
          status: c.status, content: '',  // content loaded lazily below
          sentiment: c.sentiment || 0,
        })));
        setCharacters(chars);
        setActiveCharacterId(chars[0]?.id || null);
        const activeCh = chs[0];
        setActiveChapterId(activeCh?.id || null);
        if (vs?.variables && Object.keys(vs.variables).length) setVariables({ ...initialVariables(), ...vs.variables });
        if (vs?.toggles  && Object.keys(vs.toggles).length)   setToggles({ ...DEFAULT_TOGGLES, ...vs.toggles });
        if (vs?.mode) setActiveMode(vs.mode);
        chatState.setChat(Array.isArray(msgs)
          ? msgs.map((m) => ({ id: m.id, from: m.from || m.from_, text: m.text }))
          : []);
        if (!silent) toast.success(`Opened project: ${p.name}`);
      });

      // Lazy-load active chapter content after the UI has painted.
      // This keeps the initial render fast; the editor shows content ~200ms later.
      const activeCh = chs[0];
      if (activeCh?.id) {
        chaptersApi.get(activeCh.id).then((full) => {
          setChapters((prev) =>
            prev.map((c) =>
              c.id === activeCh.id
                ? { ...c, content: full.content || '', words: full.words || c.words }
                : c,
            ),
          );
        }).catch(() => {/* soft fail — editor will stay blank until user taps chapter */});
      }
    } catch (e) {
      console.warn('loadProject failed', e);
    }
  // chatState.setChat is stable from useState; deps intentionally minimal.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildState = useNovelBuild({ project, setProject, setChapters, setActiveChapterId, loadProject, onTokensUsed: refreshTokens });

  // ------------------------------------------------------------------
  // Bootstrap: load projects, or show "Start your first novel" prompt
  // ------------------------------------------------------------------
  // New users start with NO auto-created project. The AppShell detects
  // `project === null && !loading` and renders FirstNovelPrompt, which
  // guides the writer through NewProjectDialog → WelcomeOverlay flow.
  // The `Demo` marketing tour (accessed from the landing page) is a
  // separate route and still opens a fully-populated example.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let list = await projectsApi.list();
        let active = list[0];
        if (!active) {
          // Brand-new user — no projects yet. Render blank workspace
          // with "Start your first novel" prompt instead of seeding
          // demo data that the writer then has to delete.
          if (!cancelled) { setProjects([]); setLoading(false); }
          return;
        }
        const requestedProjectId = new URLSearchParams(window.location.search).get('project');
        if (requestedProjectId) {
          const requestedProject = list.find((candidate) => candidate.id === requestedProjectId);
          if (requestedProject) {
            active = requestedProject;
          } else {
            // Project not in the list (e.g. admin opening a project outside
            // their usual scope). Attempt a direct load — the backend will
            // apply the admin bypass and resolve it.
            try {
              if (!cancelled) {
                setProjects(list);
                setLoading(false);
              }
              await loadProject(requestedProjectId, { silent: true });
              return;
            } catch {
              // Fall through — load the most recent project instead.
            }
          }
        }
        /* iter-72i — Resume-after-upgrade. If the user clicked
         * "Resume & upgrade" on the TrialExpiredScreen and has since
         * upgraded (trial_expired flipped false), open their intended
         * project on this bootstrap instead of the most-recent one.
         * Cleared once consumed so subsequent sessions behave
         * normally. */
        const intent = readResumeIntent();
        if (intent) {
          const wanted = list.find((p) => p.id === intent.project_id);
          if (wanted) active = wanted;
          clearResumeIntent();
        }
        if (cancelled) return;
        setProjects(list);
        await loadProject(active.id, { silent: true });
      } catch (e) {
        console.warn('Bootstrap failed; running on local seed only.', e);
        toast.error('Could not reach backend — running in offline preview mode.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadProject]);

  const refreshProjects = useCallback(async () => {
    try { setProjects(await projectsApi.list()); }
    catch (err) { console.warn('[store] refreshProjects failed', err); }
  }, []);

  // ----- Variables / Toggles / Mode persistence (debounced) -----
  const persistTimer = useRef(null);
  const queuePersist = useCallback((patch) => {
    if (!project) return;
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      variablesApi.update(project.id, patch).catch((err) => {
        console.warn('[store] variables persist failed', err);
        toast.error('Could not save your changes. Will retry on next edit.');
      });
    }, 600);
  }, [project]);

  const setVariable = (id, v) => {
    setVariables((prev) => {
      const next = { ...prev, [id]: v };
      queuePersist({ variables: next });
      return next;
    });
  };
  const setToggle = (k, v) => {
    setToggles((prev) => {
      const next = { ...prev, [k]: v };
      queuePersist({ toggles: next });
      return next;
    });
  };
  const setMode = (m) => {
    setActiveMode(m);
    queuePersist({ mode: m });
    // iter55f85 — Analytics: track which mode/panel writers linger in.
    // Async import so the analytics tracker doesn't get pulled into the
    // primary bundle if the writer opts out.
    if (m) {
      import('@/lib/analytics').then(({ trackPanelFocus }) => trackPanelFocus(`mode:${m}`))
        .catch(() => { /* silent — analytics is fire-and-forget */ });
    }
  };

  // ----- Character updates persist -----
  const updateCharacter = useCallback(async (id, patch) => {
    setCharacters((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    try { await charactersApi.update(id, patch); }
    catch (err) {
      console.warn('[store] updateCharacter failed', err);
      toast.error('Could not save character changes.');
    }
  }, []);

  // iter55t — Character CRUD from the Bible panel.
  const addCharacter = useCallback(async (overrides = {}) => {
    if (!project) {
      toast.error('Open a project first.');
      return null;
    }
    const body = {
      name:        overrides.name        || 'New Character',
      role:        overrides.role        || 'Supporting',
      age:         overrides.age         ?? 30,
      archetype:   overrides.archetype   || '',
      color:       overrides.color       || 'chart-3',
      description: overrides.description || '',
      voice:       overrides.voice       || '',
      traits: overrides.traits || [
        { k: 'Bravery',    v: 50 },
        { k: 'Cunning',    v: 50 },
        { k: 'Compassion', v: 50 },
        { k: 'Ambition',   v: 50 },
        { k: 'Wit',        v: 50 },
      ],
    };
    try {
      const created = await charactersApi.create(project.id, body);
      setCharacters((prev) => [...prev, created]);
      setActiveCharacterId(created.id);
      toast.success(`Added ${created.name}.`);
      return created;
    } catch (err) {
      console.warn('[store] addCharacter failed', err);
      toast.error(err?.response?.data?.detail || 'Could not add character.');
      return null;
    }
  }, [project]);

  const removeCharacter = useCallback(async (id) => {
    const doomed = characters.find((c) => c.id === id);
    // Optimistic pop, restore on failure.
    setCharacters((prev) => prev.filter((c) => c.id !== id));
    setActiveCharacterId((cur) => (cur === id
      ? (characters.find((c) => c.id !== id)?.id || null)
      : cur));
    try {
      await charactersApi.remove(id);
      toast.success(`Removed ${doomed?.name || 'character'}.`);
    } catch (err) {
      console.warn('[store] removeCharacter failed', err);
      toast.error(err?.response?.data?.detail || 'Could not remove character.');
      if (doomed) setCharacters((prev) => [...prev, doomed]);
    }
  }, [characters]);

  // iter55x — Apply one of the built-in Starter Templates (three-act,
  // hero's journey, save-the-cat, story-circle) to the active project.
  // Updates existing chapters in-place (title + status="outline" + a
  // starter beat_note as content) and creates a stub character per role
  // via the standard `charactersApi.create`. Reloads the project when
  // done so all panels reflect the new state.
  const applyStarterTemplate = useCallback(async (tpl) => {
    if (!project || !tpl) return null;
    try {
      // 1. Grow the project's chapters up to `tpl.chapters.length` so
      //    no template acts are ever silently truncated (iter55g50 —
      //    the previous MIN() behaviour dropped 10 beats of Save-the-Cat
      //    onto a 5-chapter project without warning). If the project
      //    has MORE chapters than the template, later chapters stay
      //    as-is.
      const chs      = await chaptersApi.list(project.id);
      const needed   = tpl.chapters.length;
      let   added    = 0;
      if (needed > chs.length) {
        added = needed - chs.length;
        await projectsApi.update(project.id, {
          chapters_count: needed,
          words_per_chapter: project.words_per_chapter,
        });
      }
      const chs2 = added > 0 ? await chaptersApi.list(project.id) : chs;
      for (let i = 0; i < needed; i += 1) {
        const beat = tpl.chapters[i];
        if (!chs2[i]) break;
        await chaptersApi.update(chs2[i].id, {
          title:   beat.title,
          status:  'outline',
          content: `[${beat.title}]\n\n${beat.beat_note}`,
        });
      }
      // 2. Create character stubs — OR patch existing ones when the
      //    project already has enough characters. iter55g52 Multi-Book
      //    Character Import: this way a Sequel-to template inherits
      //    Book 1's full character bible instead of duplicating stubs.
      let existingChars = [];
      try { existingChars = await charactersApi.list(project.id) || []; }
      catch (_e) { existingChars = []; }
      const inheritMode = existingChars.length >= tpl.characters.length && tpl.characters.length > 0;
      for (let i = 0; i < tpl.characters.length; i += 1) {
        const c = tpl.characters[i];
        if (inheritMode && existingChars[i]) {
          await charactersApi.update(existingChars[i].id, {
            // Merge template's role/archetype context into the existing
            // character's bible so the sequel-arc frame is applied
            // without overwriting the author's Book 1 name/age.
            archetype: c.archetype,
            role:      c.role,
            description: (existingChars[i].description || '') +
              (existingChars[i].description ? '\n\n— Sequel arc —\n' : '') + c.description,
            traits: c.traits,
          });
        } else {
          await charactersApi.create(project.id, {
            name: c.name, role: c.role, age: c.age, archetype: c.archetype,
            color: c.color, description: c.description, voice: '',
            traits: c.traits, arc: [],
          });
        }
      }
      await loadProject(project.id, { silent: true });
      toast.success(`Applied template: ${tpl.name}`, {
        description:
          `${needed} chapter outline${needed === 1 ? '' : 's'}` +
          (added > 0 ? ` (+${added} new)` : '') +
          ` + ${tpl.characters.length} character ${inheritMode ? 'update' : 'stub'}${tpl.characters.length === 1 ? '' : 's'} ` +
          (inheritMode ? 'appended to Book 1 bible' : 'loaded'),
      });
      return true;
    } catch (err) {
      console.warn('[store] applyStarterTemplate failed', err);
      toast.error(err?.response?.data?.detail || 'Could not apply template.');
      return false;
    }
  }, [project, loadProject]);

  // ----- Project create / switch / update -----
  const createProject = useCallback(async (body) => {
    const p = await projectsApi.create(body);
    await refreshProjects();
    await loadProject(p.id);
    return p;
  }, [loadProject, refreshProjects]);

  const updateProject = useCallback(async (patch) => {
    if (!project) return;
    const p = await projectsApi.update(project.id, patch);
    setProject(p);
    await refreshProjects();
    if (patch.chapters_count) {
      const chs = await chaptersApi.list(p.id);
      setChapters(chs);
    }
  }, [project, refreshProjects]);

  const value = useMemo(() => ({
    // theme
    themeMode, setThemeMode,
    // loading
    loading,
    // project
    project, projects, refreshProjects, loadProject, createProject, updateProject, setProject,
    // mode + variables + toggles
    activeMode, setActiveMode: setMode,
    variables, setVariable,
    toggles, setToggle,
    // chapters + characters
    chapters, setChapters, activeChapterId, setActiveChapterId,
    characters, setCharacters, activeCharacterId, setActiveCharacterId, updateCharacter, addCharacter, removeCharacter, applyStarterTemplate,
    // tokens
    tokens, setTokens, tokenPct, addTokens, refreshTokens,
    // chat (from hook)
    chat: chatState.chat,
    sendChat: chatState.sendChat,
    clearChat: chatState.clearChat,
    chatBusy: chatState.chatBusy,
    streamingChatText: chatState.streamingChatText,
    // build + streaming generation (from hook)
    building: buildState.building,
    buildProgress: buildState.buildProgress,
    buildJob: buildState.buildJob,
    rebuildNovel: buildState.rebuildNovel,
    generateChapter: buildState.generateChapter,
    streamingChapterId: buildState.streamingChapterId,
    streamingText: buildState.streamingText,
    // static meta
    modes: MODES, variableGroups: VARIABLE_GROUPS, togglesMeta: TOGGLES,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    themeMode, loading, project, projects, activeMode, variables, toggles,
    chapters, activeChapterId, characters, activeCharacterId,
    tokens, tokenPct,
    chatState.chat, chatState.chatBusy, chatState.streamingChatText,
    buildState.building, buildState.buildProgress, buildState.buildJob,
    buildState.streamingChapterId, buildState.streamingText,
  ]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const usePW = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error('usePW must be used inside ProWriterProvider');
  return v;
};
